import React, { useState } from 'react';
import { Search, Trophy } from 'lucide-react';
import { usePoints } from '../../hooks/usePoints';
import { useCustomers } from '../../hooks/useCustomers';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';

const PointsHistory = () => {
  const { pointsHistory, loading: historyLoading } = usePoints();
  const { customers, loading: customersLoading } = useCustomers();
  const [searchTerm, setSearchTerm] = useState('');

  const loading = historyLoading || customersLoading;

  const filteredHistory = pointsHistory.filter(history => {
    const customer = customers.find(c => c.id === history.customerId);
    if (!customer) return false;
    
    if (searchTerm) {
      return customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             customer.cpf.includes(searchTerm);
    }
    
    return true;
  });

  if (loading) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Histórico de Pontos</h2>
      
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="space-y-4">
          {filteredHistory.map(history => {
            const customer = customers.find(c => c.id === history.customerId);
            if (!customer) return null;
            
            // Determinar se é um resgate (vermelho) ou adição (verde)
            // referral_bonus sempre deve ser tratado como positivo, mesmo se o valor estiver negativo por erro
            const isRedemption = (history.type === 'redeem' || history.points < 0) && history.type !== 'referral_bonus';
            const pointsValue = Math.abs(history.points);
            const borderColor = isRedemption ? 'border-red-500' : 'border-green-500';
            const pointsColor = isRedemption ? 'text-red-600' : 'text-green-600';
            const pointsPrefix = isRedemption ? '-' : '+';
            
            return (
              <div key={history.id} className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg border-l-4 ${borderColor}`}>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{customer.name}</p>
                      <p className="text-sm text-gray-600">{formatters.formatCpfOrCnpj(customer.cpf)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${pointsColor}`}>
                        {pointsPrefix}{pointsValue} pontos
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatters.formatDate(history.date || history.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Descrição:</span> {history.description || history.reason}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatters.formatDateTime(history.date || history.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          
          {filteredHistory.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>Nenhum histórico de pontos encontrado</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointsHistory;